import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button } from 'seagull/components/Button';
import { IconButton } from 'seagull/components/IconButton';
import { Box, Column, Row } from 'seagull/components/Layout';
import { ProgressBar } from 'seagull/components/ProgressBar';
import { Tooltip } from 'seagull/components/Tooltip';
import { Typography } from 'seagull/components/Typography';
import { CloseCross, Layers, QuestionCircle } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { hasRunResults } from '../../../runResults.utils';
import { isTerminal } from '../../../taskModel';
import { useCancelRun } from '../useCancelRun';
import styles from './BulkCreateDraftsTaskItem.module.scss';
import { bulkCreateSubtitle, bulkCreateTitle } from './BulkCreateDraftsTaskItem.utils';
import { useOpenCreateRunResults } from './hooks/useOpenCreateRunResults';
const CREATE_RUN_CANCEL_BI = {
    keep: 'bulk_create_run_cancel_keep',
    confirm: 'bulk_create_run_cancel_confirm'
};
export const GetBulkCreateDraftsTaskItemTID = (parentTestId)=>getTestIds(parentTestId);
export const BulkCreateDraftsTaskItem = (param)=>{
    let { task, testId } = param;
    const { t: tasksDrawerTexts } = useLocaleKeys('navigation.tasksDrawer');
    const { BulkCreateDraftsTaskItemTestIds } = useMemo(()=>GetBulkCreateDraftsTaskItemTID(testId), [
        testId
    ]);
    const openDetails = useOpenCreateRunResults(task);
    const hasResults = hasRunResults(task);
    const isRunning = !isTerminal(task.status);
    const { api } = useStoreActions();
    const { cancelRun } = useCancelRun({
        runId: task.jobId,
        texts: tasksDrawerTexts.bulkCreateDrafts,
        biNames: CREATE_RUN_CANCEL_BI,
        cancel: api.draft.bulkCreate.cancelRun
    });
    const isUnreadOutcome = isTerminal(task.status) && task.status !== 'cancelled' && !task.seen;
    return /*#__PURE__*/ _jsxs(Row, {
        gap: 16,
        alignItems: "center",
        testId: BulkCreateDraftsTaskItemTestIds.Root(),
        className: styles.row,
        onClick: hasResults ? ()=>openDetails() : undefined,
        children: [
            /*#__PURE__*/ _jsx(Box, {
                className: styles.pageType,
                alignItems: "center",
                justifyContent: "center",
                children: /*#__PURE__*/ _jsx(Layers, {
                    size: 24
                })
            }),
            /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                className: styles.body,
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        gap: 6,
                        alignItems: "center",
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                className: styles.title,
                                variant: "paragraph2",
                                weight: "bold",
                                ellipsis: true,
                                testId: BulkCreateDraftsTaskItemTestIds.Title(),
                                children: bulkCreateTitle(task, tasksDrawerTexts.bulkCreateDrafts)
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: tasksDrawerTexts.bulkCreateDrafts.frozenTemplate(),
                                children: /*#__PURE__*/ _jsx(Box, {
                                    className: styles.frozenTemplate,
                                    testId: BulkCreateDraftsTaskItemTestIds.FrozenTemplate(),
                                    children: /*#__PURE__*/ _jsx(QuestionCircle, {
                                        size: 16
                                    })
                                })
                            }),
                            isUnreadOutcome && /*#__PURE__*/ _jsx(Box, {
                                className: styles.statusDot,
                                testId: BulkCreateDraftsTaskItemTestIds.StatusDot()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        testId: BulkCreateDraftsTaskItemTestIds.Subtitle(),
                        children: bulkCreateSubtitle(task, tasksDrawerTexts.bulkCreateDrafts)
                    }),
                    isRunning && /*#__PURE__*/ _jsxs(Row, {
                        gap: 8,
                        alignItems: "center",
                        className: styles.progressRow,
                        testId: BulkCreateDraftsTaskItemTestIds.ProgressRow(),
                        children: [
                            /*#__PURE__*/ _jsx(ProgressBar, {
                                className: styles.progressBar,
                                completed: task.progress,
                                total: 100,
                                testId: BulkCreateDraftsTaskItemTestIds.ProgressBarSlot()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                skin: "secondary",
                                children: tasksDrawerTexts.common.percent({
                                    value: task.progress
                                })
                            })
                        ]
                    })
                ]
            }),
            isRunning && /*#__PURE__*/ _jsx(IconButton, {
                skin: "transparent",
                size: "small",
                "aria-label": tasksDrawerTexts.bulkCreateDrafts.cancelRun(),
                bi: {
                    name: 'bulk_create_run_cancel'
                },
                testId: BulkCreateDraftsTaskItemTestIds.CancelButton(),
                onClick: ()=>void cancelRun(),
                children: /*#__PURE__*/ _jsx(CloseCross, {
                    size: 20
                })
            }),
            hasResults && /*#__PURE__*/ _jsx(Button, {
                className: styles.detailsButton,
                size: "small",
                bi: {
                    name: 'bulk_create_run_details'
                },
                testId: BulkCreateDraftsTaskItemTestIds.DetailsButton(),
                onClick: openDetails,
                children: tasksDrawerTexts.bulkCreateDrafts.detailsButton()
            })
        ]
    });
};
